import Phaser from 'phaser';

import { getImagePlacement } from '../utils/imagePlacement';

import type { ImagePivot, RelativeImageOffset } from '../../types';
import type { Point } from './StrokeEvaluator';
import type { TracingGameSoundController } from './TracingGameSoundController';

type PathFollowerSoundEffects = Pick<
	TracingGameSoundController,
	'playPickup' | 'playOutOfInk' | 'startPathFollow' | 'stopPathFollow'
>;

type PathFollowerConfig = {
	scene: Phaser.Scene;
	parent: Phaser.GameObjects.Container;
	x: number;
	y: number;
	textureKey: string;
	rewardTextureKey: string;
	soundEffects: PathFollowerSoundEffects;
	pivot?: ImagePivot;
};

const REWARDS = {
	alpha: { start: 1, end: 0 },
	emitting: false,
	gravityY: 200,
	lifespan: 1200,
	scale: { min: 0.8, max: 1.1 },
	rotate: { min: 0, max: 360 },
	speedX: { min: -120, max: 120 },
	speedY: { min: -500, max: -340 },
};

const ANIMATION = {
	entrance: { duration: 900, ease: 'Back.Out', easeParams: [1.2] },
	entranceVerticalOffset: 300,
	replay: { ease: 'Linear', completeDelay: 250 },
	failedReplay: { ease: 'Sine.Out' },
	replaySecondsPerUnit: 0.0012,
	replayRotationSmoothingMs: 100,
	jump: { duration: 650, ease: 'Sine.InOut' },
	replayJump: { duration: 200, height: 40 },
	jumpHeight: 180,
	fall: { duration: 900, ease: 'Back.In', easeParams: [0.6] },
	fallDrift: 100,
	fallRotation: 0.6,
};

export class PathFollower {
	private readonly image: Phaser.GameObjects.Image;
	private readonly scene: Phaser.Scene;
	private readonly parent: Phaser.GameObjects.Container;
	private tween?: Phaser.Tweens.Tween;
	private readonly rewardEmitter: Phaser.GameObjects.Particles.ParticleEmitter;
	private readonly soundEffects: PathFollowerSoundEffects;
	private playbackId = 0;

	constructor({
		scene,
		parent,
		x,
		y,
		textureKey,
		rewardTextureKey,
		soundEffects,
		pivot = [0.5, 0.5],
	}: PathFollowerConfig) {
		this.scene = scene;
		this.parent = parent;
		this.soundEffects = soundEffects;
		this.image = scene.add.image(0, 0, textureKey).setDepth(1);
		this.image.setOrigin(...pivot);
		this.setPosition(x, y);
		parent.add(this.image);
		this.rewardEmitter = scene.add.particles(0, 0, rewardTextureKey, REWARDS);
		parent.add(this.rewardEmitter);
		this.image.once(Phaser.GameObjects.Events.DESTROY, () => {
			this.stop();
			this.rewardEmitter.destroy();
		});
	}

	enter(
		start: Phaser.Types.Math.Vector2Like,
		startDirection: Phaser.Types.Math.Vector2Like,
		onComplete: () => void,
		offset?: RelativeImageOffset,
	) {
		this.stop();
		this.rewardEmitter.killAll();
		const flipX = startDirection.x < 0;
		this.image.setFlipX(flipX).setRotation(0).setAlpha(1);
		const position = getImagePlacement(start, startDirection, this.image, -1, offset);
		const offscreen = this.parent.getWorldTransformMatrix().applyInverse(flipX ? this.scene.scale.width : 0, 0);
		this.image.setPosition(
			offscreen.x + (flipX ? this.image.displayWidth : -this.image.displayWidth),
			position.y + ANIMATION.entranceVerticalOffset,
		);
		this.tween = this.scene.tweens.add({
			...ANIMATION.entrance,
			targets: this.image,
			x: position.x,
			y: position.y,
			onComplete,
		});
	}

	follow(
		points: readonly [Point, ...Point[]],
		checkpoints: readonly { point: Point; distance: number }[],
		onComplete: () => void,
		{ failed = false, onCheckpoint }: { failed?: boolean; onCheckpoint?: (index: number) => void } = {},
	) {
		this.stop();
		const playbackId = this.playbackId;
		let nextCheckpoint = 0;
		let reachedEnd = false;
		const [start, ...remainingPoints] = points;
		const path = new Phaser.Curves.Path(start.x, start.y);
		remainingPoints.forEach(({ x, y }) => path.lineTo(x, y));
		const length = path.getLength();
		const point = new Phaser.Math.Vector2();
		const tangent = new Phaser.Math.Vector2();
		const updatePose = (progress: number, delta: number) => {
			if (playbackId !== this.playbackId) return;
			if (path.getPoint(progress, point)) {
				this.setPosition(point.x, point.y);
			}
			if (path.getTangent(progress, tangent) && tangent.lengthSq() > 0) {
				const flipX = tangent.x < 0;
				const targetAngle = Phaser.Math.Angle.Wrap(tangent.angle() - (flipX ? Math.PI : 0));
				const angleDifference = targetAngle - this.image.rotation;
				const blend = 1 - Math.exp(-delta / ANIMATION.replayRotationSmoothingMs);
				this.image.setFlipX(flipX);
				this.image.setRotation(this.image.rotation + angleDifference * blend);
			}
			const distance = progress * length;
			while (nextCheckpoint < checkpoints.length && checkpoints[nextCheckpoint].distance <= distance) {
				const { point } = checkpoints[nextCheckpoint++];
				this.rewardEmitter.emitParticleAt(point.x, point.y, 1);
				this.soundEffects.playPickup();
				onCheckpoint?.(nextCheckpoint - 1);
			}
			if (progress >= 1 && !reachedEnd) {
				reachedEnd = true;
				if (failed) this.soundEffects.playOutOfInk();
			}
		};
		const startDirection = path.getTangent(0) ?? { x: 1, y: 0 };
		this.animateJump(
			start,
			startDirection,
			() => {
				updatePose(0, 0);
				this.soundEffects.startPathFollow();
				this.tween = this.scene.tweens.addCounter({
					...ANIMATION.replay,
					...(failed ? ANIMATION.failedReplay : {}),
					duration: Math.max(1, length * ANIMATION.replaySecondsPerUnit * 1000),
					from: 0,
					to: 1,
					onUpdate: (tween) => {
						updatePose(tween.getValue() ?? 0, this.scene.game.loop.delta);
					},
					onComplete: () => {
						if (playbackId !== this.playbackId) return;
						this.soundEffects.stopPathFollow();
						onComplete();
					},
				});
			},
			ANIMATION.replayJump.duration,
			ANIMATION.replayJump.height,
		);
	}

	jumpTo(
		destination: Phaser.Types.Math.Vector2Like,
		startDirection: Phaser.Types.Math.Vector2Like,
		onComplete: () => void,
		offset?: RelativeImageOffset,
	) {
		this.stop();
		const position = getImagePlacement(destination, startDirection, this.image, -1, offset);
		this.animateJump(position, startDirection, onComplete);
	}

	jumpToGoal(destination: Phaser.Types.Math.Vector2Like, onComplete: () => void) {
		this.stop();
		const direction = { x: destination.x - this.image.x, y: destination.y - this.image.y };
		this.animateJump(
			destination,
			direction,
			onComplete,
			ANIMATION.replayJump.duration,
			ANIMATION.replayJump.height,
		);
	}

	private animateJump(
		position: Phaser.Types.Math.Vector2Like,
		startDirection: Phaser.Types.Math.Vector2Like,
		onComplete: () => void,
		duration = ANIMATION.jump.duration,
		height = ANIMATION.jumpHeight,
	) {
		const playbackId = this.playbackId;
		const fromX = this.image.x;
		const fromY = this.image.y;
		const fromRotation = this.image.rotation;
		this.tween = this.scene.tweens.addCounter({
			...ANIMATION.jump,
			duration,
			from: 0,
			to: 1,
			onUpdate: (tween) => {
				if (playbackId !== this.playbackId) return;
				const progress = tween.getValue() ?? 0;
				this.image.setRotation(Phaser.Math.Linear(fromRotation, 0, progress));
				this.setPosition(
					Phaser.Math.Linear(fromX, position.x, progress),
					Phaser.Math.Linear(fromY, position.y, progress) - Math.sin(Math.PI * progress) * height,
				);
			},
			onComplete: () => {
				if (playbackId !== this.playbackId) return;
				this.setPosition(position.x, position.y);
				this.image.setFlipX(startDirection.x < 0).setRotation(0);
				onComplete();
			},
		});
	}

	fall(onComplete: () => void) {
		this.stop();
		const direction = this.image.flipX ? -1 : 1;
		const offscreen = this.parent.getWorldTransformMatrix().applyInverse(0, this.scene.scale.height);
		this.tween = this.scene.tweens.add({
			...ANIMATION.fall,
			targets: this.image,
			x: this.image.x + direction * ANIMATION.fallDrift,
			y: offscreen.y + Math.hypot(this.image.displayWidth, this.image.displayHeight),
			rotation: this.image.rotation + direction * ANIMATION.fallRotation,
			alpha: 0,
			onComplete,
		});
	}

	stop() {
		this.playbackId++;
		this.soundEffects.stopPathFollow();
		this.tween?.remove();
		this.tween = undefined;
	}

	private setPosition(x: number, y: number) {
		this.image.setPosition(x, y);
	}
}
